/**
 * @file Student.tsx
 * @description Student player demo page. Renders MicrocoursePlayer with mock data.
 */

import { useMemo } from 'react'
import { MicrocoursePlayer } from '@/components/microcourse'
import type { MicrocourseProject } from '@ai-tutor/shared'

/** Mock lesson data in unified MicrocourseProject format */
const mockProject: MicrocourseProject = {
  id: 'demo_lesson_001',
  title: '勾股定理入门示例',
  theme: 'edu-light',
  customColors: { primary: '#3b82f6', accent: '#8b5cf6', bg: '#ffffff' },
  fontFamily: "'Noto Sans SC', sans-serif",
  transition: 'slide',
  fontScale: 1,
  chapters: [{ id: 'ch1', name: '勾股定理', collapsed: false }],
  slides: [
    {
      id: 's1',
      title: '什么是勾股定理？',
      chapterId: 'ch1',
      order: 0,
      elements: [
        { id: 'e1', type: 'heading', content: '什么是勾股定理？', level: 2 },
        { id: 'e2', type: 'paragraph', content: '从一个简单的直角三角形开始' },
      ],
      segments: [{ ttsText: '今天我们来学习勾股定理，这是几何中最重要的定理之一。', lines: [] }],
    },
    {
      id: 's2',
      title: '勾股定理的内容',
      chapterId: 'ch1',
      order: 1,
      elements: [
        { id: 'e3', type: 'heading', content: '勾股定理的内容', level: 2 },
        { id: 'e4', type: 'paragraph', content: '在直角三角形中，斜边的平方等于两条直角边的平方和。这条规则叫做勾股定理，是几何中最重要的定理之一。通过这个定理，我们可以根据两条边求出第三条边的长度，也可以判断一个三角形是否为直角三角形。' },
        { id: 'e5', type: 'image', content: 'https://pub-cdn.sider.ai/u/U005HEX4EL7/web-coder/697b61ce3838a968d1a26507/resource/046bd656-42d4-43b3-b0e1-21df3506592e.jpg', caption: '直角三角形示意图', alt: '直角三角形' },
      ],
      segments: [{ ttsText: '在直角三角形中，斜边的平方等于两条直角边的平方和。', lines: [] }],
    },
    {
      id: 's3',
      title: '小测验',
      chapterId: 'ch1',
      order: 2,
      elements: [
        { id: 'e6', type: 'heading', content: '小测验', level: 2 },
        {
          id: 'e7',
          type: 'quiz',
          content: {
            type: 'choice',
            stem: '下面哪个式子正确表示了勾股定理？',
            options: [
              { label: 'A', content: 'a + b = c' },
              { label: 'B', content: 'a² + b² = c²' },
              { label: 'C', content: 'a² = b² + c²' },
            ],
            answer: { value: 'B' },
            analysis: '在直角三角形中，两条直角边为 a、b，斜边为 c，勾股定理为 a² + b² = c²。',
          },
        },
      ],
      segments: [],
    },
  ],
}

export default function Student() {
  const project = useMemo(() => mockProject, [])
  return (
    <div className="h-screen flex flex-col">
      <MicrocoursePlayer project={project} />
    </div>
  )
}
